import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { User, LoginRequest, LoginResponse, UpdateProfileRequest, UserProfile, CurrentUserResponse } from '../../types/auth';
import { authAPI } from '../../services/api/authAPI';

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  profile: {
    name: string;
    description: string;
    permissions: string[];
  } | null;
  accessibleRoutes: string[];
  restrictions: {
    navigation: string[];
    buttons: string[];
    pages: string[];
    actions: string[];
  };
}

const initialState: AuthState = {
  user: null,
  token: localStorage.getItem('auth_token'),
  isAuthenticated: !!localStorage.getItem('auth_token'),
  isLoading: false,
  error: null,
  profile: null,
  accessibleRoutes: [],
  restrictions: {
    navigation: [],
    buttons: [],
    pages: [],
    actions: []
  }
};

export type LoginErrorPayload = {
  code: string;
  message: string;
};

// Async thunks
export const login = createAsyncThunk(
  'auth/login',
  async (credentials: LoginRequest, { rejectWithValue }) => {
    try {
      const response = await authAPI.login(credentials);
      return response;
    } catch (error: any) {
      const detail =
        error?.response?.data?.detail ||
        (typeof error?.message === 'string' ? error.message : undefined);

      if (error?.response?.status === 401 || error?.status === 401) {
        if (detail === 'Email not verified') {
          return rejectWithValue({
            code: 'EMAIL_NOT_VERIFIED',
            message: 'Tu correo no está verificado. Reenvía el correo de verificación.',
          } as LoginErrorPayload);
        }
        return rejectWithValue({
          code: 'INVALID_CREDENTIALS',
          message: 'Usuario o contraseña incorrectos',
        } as LoginErrorPayload);
      }
      if (error?.response?.status === 403 || error?.status === 403) {
        return rejectWithValue({
          code: 'FORBIDDEN',
          message: 'Acceso denegado. Contacte al administrador',
        } as LoginErrorPayload);
      }
      if (error?.response?.status === 429 || error?.status === 429) {
        return rejectWithValue({
          code: 'RATE_LIMITED',
          message: 'Demasiados intentos. Espere unos minutos antes de volver a intentar',
        } as LoginErrorPayload);
      }
      if ((error?.response?.status ?? error?.status ?? 0) >= 500) {
        return rejectWithValue({
          code: 'SERVER_ERROR',
          message: 'Error del servidor. Inténtelo más tarde',
        } as LoginErrorPayload);
      }
      if (error?.code === 'NETWORK_ERROR' || error?.message?.includes('Network')) {
        return rejectWithValue({
          code: 'NETWORK_ERROR',
          message: 'Error de conexión. Verifique su conexión a internet',
        } as LoginErrorPayload);
      }

      return rejectWithValue({
        code: 'LOGIN_FAILED',
        message: 'No se pudo iniciar sesión. Inténtelo nuevamente',
      } as LoginErrorPayload);
    }
  }
);

export const logout = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      await authAPI.logout();
      return true; // Return success indicator
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Logout failed');
    }
  }
);

export const getCurrentUser = createAsyncThunk(
  'auth/getCurrentUser',
  async (_, { rejectWithValue }) => {
    try {
      const response = await authAPI.getCurrentUser();
      return response;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to get user');
    }
  }
);

export const updateProfile = createAsyncThunk(
  'auth/updateProfile',
  async (profileData: UpdateProfileRequest, { rejectWithValue }) => {
    try {
      const updatedUser = await authAPI.updateProfile(profileData);
      return updatedUser;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to update profile');
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setToken: (state, action: PayloadAction<string>) => {
      state.token = action.payload;
      state.isAuthenticated = true;
      localStorage.setItem('auth_token', action.payload);
    },
    clearAuth: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.profile = null;
      state.accessibleRoutes = [];
      state.restrictions = {
        navigation: [],
        buttons: [],
        pages: [],
        actions: []
      };
      localStorage.removeItem('auth_token');
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action: PayloadAction<LoginResponse>) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.token = action.payload.access_token;
        state.isAuthenticated = true;
        
        // Store profile information and permissions
        if (action.payload.profile) {
          state.profile = action.payload.profile;
        }
        if (action.payload.accessible_routes) {
          state.accessibleRoutes = action.payload.accessible_routes;
        }
        if (action.payload.restrictions) {
          state.restrictions = action.payload.restrictions;
        }
        
        localStorage.setItem('auth_token', action.payload.access_token);
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        const payload = action.payload as LoginErrorPayload | string | undefined;
        state.error =
          typeof payload === 'string'
            ? payload
            : payload?.message || 'Error al iniciar sesión';
        state.isAuthenticated = false;
      })
      // Logout
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        state.profile = null;
        state.accessibleRoutes = [];
        state.restrictions = {
          navigation: [],
          buttons: [],
          pages: [],
          actions: []
        };
        localStorage.removeItem('auth_token');
      })
      // Get current user
      .addCase(getCurrentUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getCurrentUser.fulfilled, (state, action: PayloadAction<CurrentUserResponse>) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.isAuthenticated = true;
        
        // Store profile information and permissions from /me endpoint
        if (action.payload.profile) {
          state.profile = action.payload.profile;
        }
        if (action.payload.accessible_routes) {
          state.accessibleRoutes = action.payload.accessible_routes;
        }
        if (action.payload.restrictions) {
          state.restrictions = action.payload.restrictions;
        }
      })
      .addCase(getCurrentUser.rejected, (state) => {
        state.isLoading = false;
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        localStorage.removeItem('auth_token');
      })
      // Update profile
      .addCase(updateProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateProfile.fulfilled, (state, action: PayloadAction<User>) => {
        state.isLoading = false;
        state.user = action.payload;
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, setToken, clearAuth } = authSlice.actions;
export default authSlice.reducer;
